import { accessControlListApiRef } from '@agilelab/plugin-wb-access-control';
import { EntityRefBasePicker } from '@agilelab/plugin-wb-platform';
import { useApi } from '@backstage/core-plugin-api';
import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { extractCustomProperties, isHidden } from '../../utils';
import { ACCESS_CONTROL_CONTEXT_FORM_CONTEXT_PATH } from '../DescriptorPicker/sourceTypes';
import { AccessControlListPickerProps } from './schema';

export const AccessControlListPicker = ({
  name,
  formContext,
  onChange,
  required,
  disabled,
  schema,
  idSchema,
  placeholder,
  uiSchema,
}: AccessControlListPickerProps) => {
  const accessControlListClient = useApi(accessControlListApiRef);
  const [configError, setConfigError] = useState<Error | undefined>(undefined);
  const customProps = extractCustomProperties(uiSchema);
  const max = uiSchema['ui:options']?.max;

  const { value: options } = useAsync(async () => {
    if (
      !formContext[ACCESS_CONTROL_CONTEXT_FORM_CONTEXT_PATH]?.id_output_port
    ) {
      setConfigError(
        new Error(
          'This field has been disabled due to a misconfiguration error. Please contact the platform team. No output port was found in the context.',
        ),
      );
      return [];
    }

    const environment =
      formContext[ACCESS_CONTROL_CONTEXT_FORM_CONTEXT_PATH].environment;
    const consumables: string[] =
      formContext[ACCESS_CONTROL_CONTEXT_FORM_CONTEXT_PATH].outputport
        .consumableInterfaces;
    try {
      // List of the ACL for each consumable
      const acls = await Promise.all(
        consumables.map(consumable =>
          accessControlListClient.getACLs(consumable, environment, false),
        ),
      );
      // Extract and deduplicate references from permissions that are not locked
      const uniqueRefs = Array.from(
        new Set(
          acls
            .flat() // combined permissions
            .flatMap(permission =>
              permission.locked ? [] : [permission.refs],
            ),
        ),
      );
      return uniqueRefs.sort((a, b) => a.localeCompare(b));
    } catch (e) {
      setConfigError(e);
      return [];
    }
  }, [formContext]);

  return (
    <EntityRefBasePicker
      options={options ?? []}
      hidden={isHidden(uiSchema)}
      prefillValues={formContext[name] ?? []}
      onChange={onChange}
      maxItems={max}
      required={required}
      disabled={disabled || (configError !== undefined ? true : false)}
      title={schema.title ?? 'Access Control List'}
      helperText={schema.description ?? 'Select the identities'}
      error={configError ? true : false}
      additionalErrors={configError ? [configError.message] : undefined}
      placeholder={placeholder}
      fieldId={idSchema.$id}
      customProps={customProps}
    />
  );
};
